import { CardContent } from '@invent/wl-ui-kit-next/ui';

import { formatDateTime } from '@/domains/survey';
import type {
  IAdminParticipantDetail,
  IAdminParticipantRegistration
} from '../../api/participant-contracts';
import { absent, instantOrAbsent } from '../../components/account-facts';
import { Fact, Section } from '../../components/admin-page';
import { personRunLabel, RunStatusBadge } from '../../components/run-status';
import { MemberPart } from './household';

/**
 * The registration runs that named any member of the household (R14) — each member's under its
 * name where there is more than one: where a run started (a person's run, the numbers it declared:
 * `personRunLabel`), where it landed when a redirect moved
 * it, how far it got, and the name the registry returned for its number (`registryName`) — FINRA's
 * or the SEC's spelling, which is what a reviewer compares with the name the registrant typed. A
 * run that resolved nobody is on no hub at all; it is the registrations queue's.
 */
export function RegistrationsSection({
  members,
  anchor
}: {
  members: IAdminParticipantDetail[];
  anchor: string;
}) {
  const multi = members.length > 1;
  return (
    <Section title="Registrations" anchor={anchor}>
      <CardContent className="grid gap-6">
        {members.map((member, index) => (
          <MemberPart key={member.participantId} member={member} opened={index === 0} multi={multi}>
            {member.registrations.length === 0 ? (
              <p className="text-sm">{absent('No registration run named this participant.')}</p>
            ) : (
              <ul className="grid gap-3">
                {member.registrations.map((run) => (
                  <RegistrationItem key={run.runId} run={run} />
                ))}
              </ul>
            )}
          </MemberPart>
        ))}
      </CardContent>
    </Section>
  );
}

function RegistrationItem({ run }: { run: IAdminParticipantRegistration }) {
  const person = personRunLabel(run.numbers);
  return (
    <li className="grid gap-3 rounded-md border border-border p-4 text-sm sm:grid-cols-2">
      <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
        <span className="font-medium">{person ?? run.survey}</span>
        {run.destinationSurvey !== run.survey && (
          <span className="text-muted-foreground">→ {run.destinationSurvey}</span>
        )}
        <RunStatusBadge status={run.status} />
      </div>
      <Fact label="Step" value={run.step} />
      {/* A person run's name is its number in hand's, gone once the run holds the number: a
          dash, never the claim that no registry named anybody. */}
      <Fact
        label="Registry’s name"
        value={
          run.registryName ??
          absent(person ? '—' : 'No registry named anybody for its number.')
        }
      />
      <Fact label="Run id" value={run.runId} />
      <Fact label="Started" value={formatDateTime(run.startedAt)} />
      <Fact label="Finished" value={instantOrAbsent(run.finishedAt, 'Still open.')} />
    </li>
  );
}
